import React, {useEffect, useId, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {CaretDown, CaretUp, Plus} from '../../icons';
import {AccountLogo} from '../../agency/accounts_v2/AccountParts';
import {platformName} from '../../../utils/filterNotes';
import {formatCompactMoney, formatFullMoney} from '../../client/panel_v11/panelFormatters';
import {
    ACTION_ADD_TO_LINE, ACTION_CHOOSE_LINE, ACTION_CREATE_LINE, DISMISS_REASONS, KIND_NO_LINE, SEARCH_FROM_LINES,
    addableCount, filterLines, isStale, lineClientsOf, linesOf, listLinesOf, primaryAction, teammatesOf, tipWord,
} from './uncontrolledModel';
import {joinNames, relativeTime, shortDate} from './uncontrolledText';

const RAW = {interpolation: {escapeValue: false}};

/** El monto en la moneda de la cuenta; en la de la agencia va compacto, en otra va entero. */
export function ownAmount(amount, currency, agencyCurrency) {
    return currency === agencyCurrency ? formatCompactMoney(currency, amount) : formatFullMoney(currency, amount);
}

function lineKeyOf(line) {
    return `${line.id}-${line.bucc_id}`;
}

/**
 * «¿A qué línea?»: el despliegue de «Sumar las N a una línea» cuando la cuenta tiene varias líneas de lista
 * (UD-5). Va EN LÍNEA, pegado a su botón —en el DOM, inmediatamente después, así es lo que sigue al tabular
 * y no «Avisarle al equipo»—: un botón por línea (con el cliente si son de más de uno) y, con más de
 * `SEARCH_FROM_LINES`, un buscador que no distingue tildes, donde Enter elige la línea si queda una sola.
 * Al abrirse, el foco va al buscador o, sin él, a la primera opción. Escape lo cierra y devuelve el foco a su
 * botón.
 *
 * `onChoose(line)` devuelve la promesa del pedido (`true` si salió). Mientras vuelve, las opciones quedan
 * deshabilitadas y el foco se cae al `body`: si el pedido falla, vuelve a la opción elegida (o al buscador)
 * cuando las opciones se habilitan de nuevo (C-cs-fe-07).
 */
function LineChooser({id, lines, busy, onChoose, onClose}) {
    const {t} = useTranslation();
    const [query, setQuery] = useState('');
    // `{key}` de la opción a la que vuelve el foco tras un pedido que falló; `null` = nada pendiente.
    const [refocus, setRefocus] = useState(null);
    const rootRef = useRef(null);
    const searchRef = useRef(null);
    const hasSearch = lines.length > SEARCH_FROM_LINES;
    const showClients = lineClientsOf(lines).length > 1;
    const options = filterLines(lines, query);

    const focusOption = (key = null) => {
        const root = rootRef.current;
        if (!root) return;
        const enabled = (selector) => root.querySelector(`${selector}:not(:disabled)`);
        const target = (key && enabled(`.alc-unc-choose__option[data-line-key="${key}"]`))
            || searchRef.current || enabled('.alc-unc-choose__option');
        if (target) target.focus({preventScroll: true});
    };

    // `preventScroll`: abrirlo no mueve la página (el despliegue ya está a la vista, debajo de su botón).
    useEffect(() => {
        focusOption();
        // Sólo al abrirse.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (busy || !refocus) return;
        focusOption(refocus.key);
        setRefocus(null);
        // `focusOption` lee refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busy, refocus]);

    const choose = (line) => {
        Promise.resolve(onChoose(line)).then((ok) => {
            if (!ok) setRefocus({key: lineKeyOf(line)});
        });
    };

    const onKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        onClose();
    };

    // Enter en el buscador con un único resultado elige esa línea (C-cs-fe-17).
    const onSearchKeyDown = (event) => {
        if (event.key !== 'Enter' || options.length !== 1 || busy) return;
        event.preventDefault();
        choose(options[0]);
    };

    return (
        <div className="alc-unc-choose" id={id} role="group" aria-labelledby={`${id}-title`} onKeyDown={onKeyDown}
             ref={rootRef}>
            <p className="alc-unc-choose__title" id={`${id}-title`}>{t('alerts_center.uncontrolled.item.choose_title')}</p>
            {hasSearch && (
                <input type="search" ref={searchRef} className="alc-unc-choose__search" value={query}
                       onChange={(event) => setQuery(event.target.value)} onKeyDown={onSearchKeyDown}
                       aria-label={t('alerts_center.uncontrolled.item.choose_search')}
                       placeholder={t('alerts_center.uncontrolled.item.choose_search')}/>
            )}
            {options.length > 0 ? (
                <ul className="alc-unc-choose__list">
                    {options.map((line) => (
                        <li key={lineKeyOf(line)}>
                            <button type="button" className="alc-unc-choose__option" disabled={busy}
                                    data-line-key={lineKeyOf(line)} onClick={() => choose(line)}>
                                <Plus size={13} weight="bold" aria-hidden="true"/>
                                <span className="alc-unc-choose__text">
                                    {t('alerts_center.uncontrolled.item.add_to_this_line', {line: line.name, ...RAW})}
                                    {showClients && line.client_name && (
                                        <span className="alc-unc-choose__client">{line.client_name}</span>
                                    )}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="alc-unc-choose__empty">
                    {t('alerts_center.uncontrolled.item.choose_empty', {query, ...RAW})}
                </p>
            )}
        </div>
    );
}

/**
 * Una cuenta con plata sin control, dentro de la tarjeta de su cliente: qué pasa, cuánto, desde cuándo,
 * la advertencia de la línea quieta y las acciones del mockup (sumar a la línea o crear una, avisarle
 * al equipo, «No hace falta controlarla» con su motivo).
 *
 * El botón principal decide con `item.lines` (UD-5, `primaryAction`): con una línea de lista suma
 * directo; con varias, «Sumar las N a una línea» despliega «¿A qué línea?»; con líneas pero ninguna de
 * lista, «Editar la línea» de la primera; sin líneas que quien mira vea, «Crear una línea». Lo resuelto
 * recuerda la línea ELEGIDA, que es la que nombra y linkea.
 *
 * El estado de cada formulario abierto es local; lo que cambia el total (resuelto, campañas descartadas,
 * avisado) lo guarda la pantalla, que es la que descuenta.
 */
export function UncontrolledItem({client, item, agencyCurrency, myUserId, state, actions}) {
    const {t, i18n} = useTranslation();
    const chooserId = useId();
    const chooseToggleRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [composing, setComposing] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [choosing, setChoosing] = useState(false);
    const [reason, setReason] = useState(DISMISS_REASONS[0]);
    const [unchecked, setUnchecked] = useState({});
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const accountName = item.account_name || item.account_id;
    const teammates = teammatesOf(client.team, myUserId);
    const dismissedCampaigns = state.dismissedCampaigns || {};
    const resolved = state.resolved;
    const flagged = state.flagged || item.flagged;
    const stale = isStale(item);
    const action = primaryAction(item);
    const lines = linesOf(item);
    const listLines = listLinesOf(item);
    const count = item.campaign_count;
    const addable = addableCount(item, dismissedCampaigns);

    // «¿A qué línea?» se baja cuando deja de corresponder: la acción ya no es elegir línea o no queda nada
    // que sumar. Si quedara en `true` escondido, una recarga que vuelve a dibujar la tarjeta lo montaba
    // abierto y le robaba el foco al botón (C-cs-fe-07).
    useEffect(() => {
        if (choosing && (action !== ACTION_CHOOSE_LINE || addable === 0)) setChoosing(false);
    }, [choosing, action, addable]);
    const keyBase = `alc-unc-${client.client_id}-${item.channel}-${item.account_id}`;

    // Todo pedido de la cuenta pasa por acá, y mientras vuelve sus botones no se aprietan: un doble clic
    // mandaba dos pedidos, y una campaña a medio descartar entraba igual en «Sumar a la línea».
    const run = (promise) => {
        setBusy(true);
        return promise.finally(() => setBusy(false));
    };

    // ------------------------------------------------------------------ textos -----------------
    const title = item.kind === KIND_NO_LINE
        ? t('alerts_center.uncontrolled.item.no_line_title')
        : t('alerts_center.uncontrolled.item.uncovered_title', {count});
    const amountText = ownAmount(item.amount, item.currency, agencyCurrency);
    const uncoveredBody = () => {
        if (lines.length === 1) {
            return t('alerts_center.uncontrolled.item.uncovered_body', {amount: amountText, count, line: lines[0].name, ...RAW});
        }
        // Plural por las campañas (`count`); el número de líneas va fijo (con varias, siempre son 2 o más).
        if (lines.length > 1) {
            return t('alerts_center.uncontrolled.item.uncovered_body_lines',
                {amount: amountText, count, lines: lines.length, ...RAW});
        }
        return t('alerts_center.uncontrolled.item.uncovered_body_none', {amount: amountText, count, ...RAW});
    };
    let body = item.kind === KIND_NO_LINE
        ? t('alerts_center.uncontrolled.item.no_line_body', {amount: amountText, count, ...RAW})
        : uncoveredBody();
    if (item.has_daily_limit) body += ` ${t('alerts_center.uncontrolled.item.has_limit_body')}`;
    const since = item.spending_since
        ? t('alerts_center.uncontrolled.item.since', {date: shortDate(item.spending_since, i18n && i18n.language), ...RAW})
        : t('alerts_center.uncontrolled.item.since_long');

    const primaryLabel = () => {
        if (action === ACTION_CREATE_LINE) return t('alerts_center.uncontrolled.item.create_line');
        if (action === ACTION_ADD_TO_LINE) {
            return t('alerts_center.uncontrolled.item.add_to_line', {count: addable, line: listLines[0].name, ...RAW});
        }
        if (action === ACTION_CHOOSE_LINE) {
            return t('alerts_center.uncontrolled.item.add_to_some_line', {count: addable});
        }
        return t('alerts_center.uncontrolled.item.edit_line');
    };

    const closeChooser = () => {
        setChoosing(false);
        if (chooseToggleRef.current) chooseToggleRef.current.focus();
    };

    const addTo = (line) => run(actions.addToLine(client, item, line));

    // ------------------------------------------------------------------ resuelto ---------------
    if (resolved) {
        return (
            <div className="alc-unc-item -resolved" data-testid="alc-unc-item" aria-busy={busy || undefined}>
                <p className="alc-unc-item__resolved" role="status">
                    {resolvedText(t, resolved, item, accountName)}
                    {resolved.kind === 'dismissed' && (
                        <button type="button" className="alc-link -button" disabled={busy}
                                onClick={() => run(actions.undoDismissAccount(client, item))}>
                            {t('alerts_center.uncontrolled.undo')}
                        </button>
                    )}
                    {resolved.kind === 'created' && resolved.lineId && (
                        <Link className="alc-link" to={`/business-unit/${resolved.lineId}/`}>
                            {t('alerts_center.uncontrolled.item.load_budget')}
                        </Link>
                    )}
                    {resolved.kind === 'added' && resolved.line && (
                        <Link className="alc-link" to={`/business-unit/${resolved.line.id}/accounts`}>
                            {t('alerts_center.uncontrolled.item.see_line')}
                        </Link>
                    )}
                </p>
            </div>
        );
    }

    // ------------------------------------------------------------------ piezas -----------------
    const renderCampaigns = () => {
        if (!expanded) return null;
        return (
            <ul className="alc-unc-camps" id={`${keyBase}-camps`}>
                {item.campaigns.map((row) => {
                    const off = !!dismissedCampaigns[row.campaign_id];
                    return (
                        <li key={row.campaign_id} className={`alc-unc-camps__row${off ? ' -off' : ''}`}>
                            <span className="alc-unc-camps__name">{row.name || row.campaign_id}</span>
                            <span className="alc-unc-camps__amount">{ownAmount(row.amount, item.currency, agencyCurrency)}</span>
                            {item.kind !== KIND_NO_LINE && (off ? (
                                <span className="alc-unc-camps__off">
                                    {t('alerts_center.uncontrolled.campaign.off')}
                                    <button type="button" className="alc-link -button" disabled={busy}
                                            onClick={() => run(actions.undoDismissCampaign(client, item, row.campaign_id))}>
                                        {t('alerts_center.uncontrolled.undo')}
                                    </button>
                                </span>
                            ) : (
                                <button type="button" className="alc-link -button alc-unc-camps__dismiss" disabled={busy}
                                        onClick={() => run(actions.dismissCampaign(client, item, row.campaign_id))}>
                                    {t('alerts_center.uncontrolled.campaign.dismiss')}
                                </button>
                            ))}
                        </li>
                    );
                })}
                {item.campaign_count > item.campaigns.length && (
                    <li className="alc-unc-camps__more">
                        {t('alerts_center.uncontrolled.campaign.more', {count: item.campaign_count - item.campaigns.length})}
                    </li>
                )}
            </ul>
        );
    };

    const renderComposer = () => (
        <form className="alc-unc-form" onSubmit={(event) => {
            event.preventDefault();
            const userIds = teammates.filter((member) => !unchecked[member.id]).map((member) => member.id);
            run(actions.notify(client, item, userIds, note)).then((ok) => { if (ok) setComposing(false); });
        }}>
            <fieldset className="alc-unc-form__group">
                <legend className="alc-unc-form__legend">
                    {t('alerts_center.uncontrolled.notify.title', {client: client.client_name, ...RAW})}
                </legend>
                {teammates.map((member) => (
                    <label key={member.id} className="alc-unc-check">
                        <input type="checkbox" checked={!unchecked[member.id]}
                               onChange={() => setUnchecked((current) => ({...current, [member.id]: !current[member.id]}))}/>
                        <span>{member.name}</span>
                    </label>
                ))}
            </fieldset>
            <label className="alc-unc-form__label" htmlFor={`${keyBase}-note`}>
                {t('alerts_center.uncontrolled.notify.note_label')}
            </label>
            <textarea id={`${keyBase}-note`} className="alc-unc-form__note" maxLength={500} rows={2}
                      value={note} onChange={(event) => setNote(event.target.value)}/>
            <p className="alc-unc-form__hint">{t('alerts_center.uncontrolled.notify.hint')}</p>
            <div className="alc-unc-actions">
                <button type="submit" className="alc-btn -primary"
                        disabled={busy || teammates.every((member) => unchecked[member.id])}>
                    {t('alerts_center.uncontrolled.notify.send')}
                </button>
                <button type="button" className="alc-btn -ghost" onClick={() => setComposing(false)}>
                    {t('alerts_center.uncontrolled.cancel')}
                </button>
            </div>
        </form>
    );

    const renderDismiss = () => (
        <form className="alc-unc-form" onSubmit={(event) => {
            event.preventDefault();
            run(actions.dismissAccount(client, item, reason)).then((ok) => { if (ok) setDismissing(false); });
        }}>
            <fieldset className="alc-unc-form__group">
                <legend className="alc-unc-form__legend">{t('alerts_center.uncontrolled.dismiss.title')}</legend>
                {DISMISS_REASONS.map((code) => (
                    <label key={code} className="alc-unc-check">
                        <input type="radio" name={`${keyBase}-reason`} value={code} checked={reason === code}
                               onChange={() => setReason(code)}/>
                        <span>{t(`alerts_center.uncontrolled.dismiss.reason.${code}`)}</span>
                    </label>
                ))}
            </fieldset>
            <div className="alc-unc-actions">
                <button type="submit" className="alc-btn -primary" disabled={busy}>
                    {t('alerts_center.uncontrolled.dismiss.confirm')}
                </button>
                <button type="button" className="alc-btn -ghost" onClick={() => setDismissing(false)}>
                    {t('alerts_center.uncontrolled.cancel')}
                </button>
            </div>
        </form>
    );

    const renderPrimary = () => {
        // Con todas las campañas de la tarjeta descartadas no queda nada que sumar: «Sumar las 0» terminaba
        // en `MISSING_CAMPAIGNS`.
        const adds = action === ACTION_ADD_TO_LINE || action === ACTION_CHOOSE_LINE;
        if (adds && addable === 0) return null;
        if (action === ACTION_CHOOSE_LINE) {
            return (
                <button type="button" ref={chooseToggleRef} className="alc-btn -primary alc-unc-choose-toggle"
                        disabled={busy && !choosing} aria-expanded={choosing}
                        aria-controls={choosing ? chooserId : undefined} data-testid="alc-unc-primary"
                        onClick={() => (choosing ? closeChooser() : setChoosing(true))}
                        onKeyDown={(event) => {
                            // Escape con el foco en el botón también cierra el despliegue abierto.
                            if (event.key !== 'Escape' || !choosing) return;
                            event.preventDefault();
                            closeChooser();
                        }}>
                    {primaryLabel()}
                    {choosing
                        ? <CaretUp size={13} weight="bold" aria-hidden="true"/>
                        : <CaretDown size={13} weight="bold" aria-hidden="true"/>}
                </button>
            );
        }
        if (action === ACTION_CREATE_LINE || action === ACTION_ADD_TO_LINE) {
            const onClick = action === ACTION_CREATE_LINE
                ? () => run(actions.createLine(client, item))
                : () => addTo(listLines[0]);
            return (
                <button type="button" className="alc-btn -primary" disabled={busy} onClick={onClick}
                        data-testid="alc-unc-primary">
                    {primaryLabel()}
                </button>
            );
        }
        return (
            <Link className="alc-btn -primary" to={`/business-unit/${lines[0].id}/accounts`} data-testid="alc-unc-primary">
                {primaryLabel()}
            </Link>
        );
    };

    const renderChooser = () => {
        if (!choosing || action !== ACTION_CHOOSE_LINE || addable === 0) return null;
        // Con el pedido OK el despliegue se baja (aunque la tarjeta pase a «resuelto» y no se vea).
        return (
            <LineChooser id={chooserId} lines={listLines} busy={busy} onClose={closeChooser}
                         onChoose={(line) => addTo(line).then((ok) => {
                             if (ok) setChoosing(false);
                             return ok;
                         })}/>
        );
    };

    const toggleLabel = expanded
        ? t('alerts_center.uncontrolled.item.hide_campaigns')
        : t('alerts_center.uncontrolled.item.show_campaigns', {count});

    return (
        <div className={`alc-unc-item${item.limit_only ? ' -limit' : ''}`} data-testid="alc-unc-item"
             aria-busy={busy || undefined}>
            <div className="alc-unc-item__top">
                <span className="alc-unc-item__account">
                    <AccountLogo channel={item.channel} size={22}/>
                    <span className="alc-unc-item__name">{accountName}</span>
                    <span className="alc-unc-item__channel">{platformName(item.channel)}</span>
                </span>
                <span className={`alc-unc-tag -${item.kind === KIND_NO_LINE ? 'no-line' : 'uncovered'}`}>
                    {t(`alerts_center.uncontrolled.tag.${item.kind}`)}
                </span>
                {item.has_daily_limit && <span className="alc-unc-tag -limit">{t('alerts_center.uncontrolled.tag.limit')}</span>}
            </div>

            <div className="alc-unc-item__main">
                <div className="alc-unc-item__text">
                    <p className="alc-unc-item__title">{title}</p>
                    <p className="alc-unc-item__body">{body}</p>
                </div>
                <div className="alc-unc-item__money">
                    <span className="alc-unc-item__amount">{amountText}</span>
                    {item.currency !== agencyCurrency && item.amount_converted !== null && (
                        <span className="alc-unc-item__approx">≈ {formatCompactMoney(agencyCurrency, item.amount_converted)}</span>
                    )}
                    <span className="alc-unc-item__since">{since}</span>
                </div>
            </div>

            {stale && (
                <div className="alc-unc-warn" role="note">
                    <strong>{t('alerts_center.uncontrolled.warning.label')}</strong>{' '}
                    {t('alerts_center.uncontrolled.warning.text', {line: item.stale_lines[0].name, ...RAW})}{' '}
                    {t('alerts_center.uncontrolled.warning.tip', {word: tipWord(item), ...RAW})}
                </div>
            )}

            {item.campaigns.length > 0 && (
                <button type="button" className="alc-link -button alc-unc-item__toggle" aria-expanded={expanded}
                        aria-controls={`${keyBase}-camps`} onClick={() => setExpanded((value) => !value)}>
                    {toggleLabel}
                </button>
            )}
            {renderCampaigns()}

            {flagged && (
                <p className="alc-unc-item__flag" role="status">{flagText(t, flagged, myUserId)}</p>
            )}

            {composing && renderComposer()}
            {dismissing && renderDismiss()}
            {!composing && !dismissing && (
                <div className="alc-unc-actions">
                    {renderPrimary()}
                    {/* Pegado a su botón en el DOM (orden de tabulación); ocupa su propio renglón. */}
                    {renderChooser()}
                    {teammates.length > 0 && (
                        <button type="button" className="alc-btn -ghost" disabled={busy}
                                onClick={() => { setChoosing(false); setComposing(true); }}>
                            {t('alerts_center.uncontrolled.item.notify')}
                        </button>
                    )}
                    <button type="button" className="alc-btn -ghost" disabled={busy}
                            onClick={() => { setChoosing(false); setDismissing(true); }}>
                        {t('alerts_center.uncontrolled.item.dismiss')}
                    </button>
                </div>
            )}
        </div>
    );
}

function resolvedText(t, resolved, item, accountName) {
    if (resolved.kind === 'added') {
        // Las que sumó el backend, no las que traía la tarjeta; y la línea ELEGIDA, no la de `item.line`.
        const count = Number.isInteger(resolved.count) ? resolved.count : item.campaign_count;
        return t('alerts_center.uncontrolled.resolved.added', {line: (resolved.line || {}).name, count, ...RAW});
    }
    if (resolved.kind === 'created') {
        return t('alerts_center.uncontrolled.resolved.created', {account: accountName, ...RAW});
    }
    return t('alerts_center.uncontrolled.resolved.dismissed', {
        reason: t(`alerts_center.uncontrolled.dismiss.reason.${resolved.reason}`).toLowerCase(), ...RAW});
}

function flagText(t, flagged, myUserId) {
    const recipients = joinNames((flagged.recipients || []).map((name) => String(name).split(' ')[0]), t);
    if (flagged.mine || (flagged.by_id && String(flagged.by_id) === String(myUserId))) {
        return t('alerts_center.uncontrolled.flag.mine', {names: recipients, ...RAW});
    }
    return t('alerts_center.uncontrolled.flag.other', {
        by: String(flagged.by || '').split(' ')[0], names: recipients, when: relativeTime(flagged.at, t), ...RAW});
}

export default UncontrolledItem;
